import React, { useState, useEffect, useRef } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import '../shared.css';
import LoadingOverlay from '../LoadingOverlay';

function PurchaseSummary() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const [totalqty, setTotalqty] = useState(0);
    const [totalamt, setTotalamt] = useState(0);
    const [totalgst, setTotalgst] = useState(0);
    const [totalnet, setTotalnet] = useState(0);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        const startDate = getFormattedStartDate();
        const currentDate = getFormattedCurrentDate();
        setFromDate(startDate);
        setToDate(currentDate);
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        if (fromDate && toDate) {
            fetchTableData(fromDate, toDate);
        }
    }, [fromDate, toDate]);

    const [totalbills, setTotalbills] = useState(0);

    useEffect(() => {
        let totalqty = 0, totalamt = 0, totalgst = 0, totalnet = 0;
        let billNumbers = new Set();
        dataItem.forEach(item => {
            totalqty += item.Quantity;
            totalamt += item.Amount;
            totalgst += item.GSTAmount;
            totalnet += item.TotalAmount;
            billNumbers.add(item.GRNNumber);
        });
        setTotalqty(totalqty);
        setTotalamt(totalamt);
        setTotalgst(totalgst);
        setTotalnet(totalnet);
        setTotalbills(billNumbers.size);
    }, [dataItem]);

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];

    const getFormattedStartDate = () => {
        const d = new Date();
        d.setDate(1);
        return d.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => setFromDate(e.target.value);
    const handleToDateChange = (e) => setToDate(e.target.value);

    const hasReadReportPermission = permissions.includes('Read Reports');
    const [loading, setLoading] = useState(false);
    const loadingRef = useRef(false);

    const fetchTableData = async (fDate, tDate) => {
        const effectiveFromDate = fDate || fromDate;
        const effectiveToDate = tDate || toDate;
        if (hasReadReportPermission && !loadingRef.current && effectiveFromDate && effectiveToDate) {
            setDataItem([]);
            loadingRef.current = true;
            setLoading(true);
            try {
                const response = await fetchData('ReportApi', `/PurchaseSummary/${companyId}/${branchId}/${effectiveFromDate}/${effectiveToDate}`, token);
                if (response.responseCode === '1111') {
                    setDataItem(response.response_data);
                }
            } catch (error) {
                console.error('Error fetching Purchase Summary Details:', error);
            } finally {
                loadingRef.current = false;
                setLoading(false);
            }
        }
    };

    const handleSearch = (event) => setSearchQuery(event.target.value);

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Purchase Summary');

        const rightAlignColumns = [
            'Quantity', 'Purchase Rate', 'GST %', 'Net Value',
            'GST Amount', 'Net Value + Tax', 'Total Bills', 'Total Qty',
            'Total Net', 'Total GST', 'Total Amount'
        ];

        const headerCaption = `Purchase Summary From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`;
        worksheet.mergeCells('A1:W1');
        const headerCaptionCell = worksheet.getCell('A1');
        headerCaptionCell.value = headerCaption;
        headerCaptionCell.font = { bold: true, size: 14 };
        headerCaptionCell.alignment = { vertical: 'middle', horizontal: 'left' };

        const headerRowValues = [
            'Purchase Type', 'GRN Date', 'GRN Number', 'LR Date', 'LR Number',
            'PG', 'IG', 'ISG', 'PF', 'Item No', 'Description', 'HSN Code',
            'Quantity', 'Net Value', 'GST %', 'GST Amount',
            'Net Value + Tax', 'Comments', 'Category', 'Item Group',
            'Season Code', 'Size Code'
        ];
        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB6D7A8' } };
            cell.font = { bold: true, color: { argb: 'FF000000' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        worksheet.columns = headerRowValues.map(() => ({ width: 18 }));

        filteredDataItem.forEach((rowData) => {
            const dataRow = [
                rowData.PurchaseType, formatDate(rowData.GRNDate), rowData.GRNNumber,
                formatDate(rowData.LRDate), rowData.LRNumber,
                rowData.PG, rowData.IG, rowData.ISG, rowData.PF, parseFloat(rowData.Code),
                rowData.ItemName, rowData.HSNCode, parseFloat(rowData.Quantity),
                parseFloat(rowData.GSTRate), parseFloat(rowData.Amount.toFixed(2)),
                parseFloat(rowData.GSTAmount.toFixed(2)), parseFloat(rowData.TotalAmount.toFixed(2)),
                rowData.Remarks, rowData.CategoryName, rowData.ItemGroupName, rowData.SeasonCode, rowData.SizeCode
            ];
            const row = worksheet.addRow(dataRow);
            row.eachCell((cell, colNumber) => {
                const headerName = headerRowValues[colNumber - 1];
                cell.alignment = { vertical: 'middle', horizontal: rightAlignColumns.includes(headerName) ? 'right' : 'center' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });

        const totalRowValues = [
            '', '', '', '', '', '', '', '', '', '',
            'Total Bills', totalbills, totalqty, totalnet, '',
            totalgst, totalamt, '', '', '', '', ''
        ];
        const totalRow = worksheet.addRow(totalRowValues);
        totalRow.font = { bold: true };
        totalRow.eachCell((cell, colNumber) => {
            const headerName = headerRowValues[colNumber - 1];
            cell.alignment = { vertical: 'middle', horizontal: rightAlignColumns.includes(headerName) ? 'right' : 'center' };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        worksheet.views = [{ state: 'frozen', ySplit: 2 }];
        worksheet.columns = headerRowValues.map(() => ({ width: 18 }));

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'PurchaseSummary.xlsx');
    };

    return (
        <div className="pg-container">
            <div className="pg-header">
                <h5 className="pg-title">Purchase Summary</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input className="pg-input" type="date" value={fromDate} onChange={handleFromDateChange} />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input className="pg-input" type="date" value={toDate} onChange={handleToDateChange} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={() => fetchTableData()} disabled={loading}>
                            <RefreshIcon /> {loading ? 'Loading...' : 'Refresh'}
                        </button>
                        <button className="pg-btn pg-btn-success" onClick={handleExportExcel}>
                            <FeedIcon /> Excel
                        </button>
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearchInput(v => !v)}>
                            <SearchIcon /> Search
                        </button>
                    </div>
                    {showSearchInput && (
                        <input className="pg-input pg-search-input" placeholder="Search…"
                            value={searchQuery} onChange={handleSearch} autoFocus style={{ marginTop: 6 }} />
                    )}
                </div>
            </div>

            {loading && <LoadingOverlay />}

            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>Purchase Type</th>
                            <th>GRN Date</th>
                            <th>GRN Number</th>
                            <th>LR Date</th>
                            <th>LR Number</th>
                            <th>PG</th>
                            <th>IG</th>
                            <th>ISG</th>
                            <th>PF</th>
                            <th>Item No</th>
                            <th>Description</th>
                            <th>HSN Code</th>
                            <th>Quantity</th>
                            <th>Net Value</th>
                            <th>GST %</th>
                            <th>GST Amount</th>
                            <th>Net Value + GST</th>
                            <th>Comments</th>
                            <th>Category</th>
                            <th>Item Group</th>
                            <th>Season Code</th>
                            <th>Size Code</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDataItem.map((data, index) => (
                            <tr key={index}>
                                <td>{data.PurchaseType}</td>
                                <td>{formatDate(data.GRNDate)}</td>
                                <td>{data.GRNNumber}</td>
                                <td>{formatDate(data.LRDate)}</td>
                                <td>{data.LRNumber}</td>
                                <td>{data.PG}</td>
                                <td>{data.IG}</td>
                                <td>{data.ISG}</td>
                                <td>{data.PF}</td>
                                <td>{data.Code}</td>
                                <td>{data.ItemName}</td>
                                <td>{data.HSNCode}</td>
                                <td style={{ textAlign: 'right' }}>{data.Quantity}</td>
                                <td style={{ textAlign: 'right' }}>{data.Amount.toFixed(2)}</td>
                                <td style={{ textAlign: 'right' }}>{data.GSTRate}</td>
                                <td style={{ textAlign: 'right' }}>{data.GSTAmount.toFixed(2)}</td>
                                <td style={{ textAlign: 'right' }}>{data.TotalAmount.toFixed(2)}</td>
                                <td>{data.Remarks}</td>
                                <td>{data.CategoryName}</td>
                                <td>{data.ItemGroupName}</td>
                                <td>{data.SeasonCode}</td>
                                <td>{data.SizeCode}</td>
                            </tr>
                        ))}
                        <tr style={{ background: '#1c2a3a', color: '#fff', fontWeight: 700 }}>
                            <td colSpan={10}></td>
                            <td>Total Bills</td>
                            <td style={{ textAlign: 'right' }}>{totalbills.toFixed(2)}</td>
                            <td style={{ textAlign: 'right' }}>{totalqty.toFixed(2)}</td>
                            <td style={{ textAlign: 'right' }}>{totalnet.toFixed(2)}</td>
                            <td></td>
                            <td style={{ textAlign: 'right' }}>{totalgst.toFixed(2)}</td>
                            <td style={{ textAlign: 'right' }}>{totalamt.toFixed(2)}</td>
                            <td colSpan={5}></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default PurchaseSummary;
